import { getConfig } from '../../config/environment';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { isTokenExpired } from '@/lib/core/utils/dateTime';

// NOTE: Supports cases where `content-type` is other than `json`
const getBody = async <T>(c: Response | Request): Promise<T | null> => {
  try {
    const text = await c.text();
    if (!text || text.trim() === '') {
      return null;
    }
    return JSON.parse(text);
  } catch (error) {
    console.error('Error parsing JSON response:', error);
    return null;
  }
};

/**
 * Aborts `controller` after `ms`, distinguishably from a deliberate cancel.
 *
 * `controller.abort()` with no reason always rejects the fetch with the same bare `AbortError`,
 * whether it came from a request timing out or from a component cleaning up a request it no longer
 * wants. The two need opposite treatment — one is a fault worth seeing, the other is the app working
 * correctly — so the timeout supplies a reason and `customFetch` keys off its name.
 *
 * Returns the timer id, for the caller to clear in its cleanup.
 */
export const abortAfter = (controller: AbortController, ms: number): ReturnType<typeof setTimeout> =>
  setTimeout(() => controller.abort(new DOMException(`Request timed out after ${ms}ms`, 'TimeoutError')), ms);

/**
 * Convert absolute URL to relative path
 */
const toRelativePath = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // If it's not a valid URL (already relative), return as is
    return url;
  }
};

export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  const config = getConfig();

  try {
    // Convert to relative path if absolute URL
    const relativePath = toRelativePath(url);

    // If the URL is relative and starts with /umbraco or /api, prepend the base URL
    const isCmsApi = relativePath.includes('/umbraco');
    const isPlatformApi = relativePath.includes('/api');
    let fullUrl = relativePath;

    if (relativePath.startsWith('/umbraco') || relativePath.startsWith('/api')) {
      const baseUrl = isCmsApi ? `${config.cmsUrl}` : `${config.platformUrl}`;

      if (!baseUrl) {
        throw new Error(
          'customFetch: No Api base URL configured. Set API_TARGET_URL and API_BASE_URL in your environment.'
        );
      }

      const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      fullUrl = `${normalizedBaseUrl}${relativePath}`;
    }

    // Convert headers to Headers object, preserving all existing headers
    const headers = new Headers();

    // First, copy all existing headers from options
    if (options.headers) {
      if (options.headers instanceof Headers) {
        // If it's already a Headers object, copy all entries
        options.headers.forEach((value, key) => {
          headers.set(key, value);
        });
      } else if (Array.isArray(options.headers)) {
        // If it's an array of [key, value] pairs
        options.headers.forEach(([key, value]) => {
          headers.set(key, value);
        });
      } else {
        // If it's a plain object
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers.set(key, value);
          } else if (Array.isArray(value)) {
            // Handle array values (e.g., ['value1', 'value2'])
            (value as string[]).forEach((v: string) => headers.append(key, String(v)));
          }
        });
      }
    }

    // Set Content-Type if not already set
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Preserve Preview header if it exists (for draft/preview content)
    const previewHeader = headers.get('Preview');

    // Add API key for Umbraco API endpoints
    const apiKey = isCmsApi ? config.cmsApiKey : config.platformApiKey;

    // Debug logging to help diagnose API key issues
    if (isCmsApi || isPlatformApi) {
      const apiType = isCmsApi ? 'Cms API' : 'Platform API';

      if (apiKey) {
        headers.set('Api-Key', apiKey);
      } else {
        console.error(
          `CustomFetch: No API key found for ${apiType}! Set VITE_CMS_API_KEY or VITE_PLATFORM_API_KEY in environment variables`
        );
        console.error(`CustomFetch: Request URL: ${fullUrl}`);
        console.error(`CustomFetch: This will likely result in a 401 Unauthorized error`);
        if (isCmsApi && previewHeader) {
          console.warn(`CustomFetch: Preview mode requires API key for authentication`);
        }
      }
    }

    // Specifically check if Api-Key header is present
    if (!headers.has('Api-Key') && (isCmsApi || isPlatformApi)) {
      console.error(
        `CustomFetch: ✗ Api-Key header is MISSING for ${isCmsApi ? 'Cms' : 'Platform'} API request!`
      );
    }

    // Inject user Bearer token for platform API calls when authenticated and token is valid
    if (isPlatformApi && !headers.has('Authorization')) {
      const { accessToken, expiresAt } = useAuthStore.getState();
      if (accessToken && !isTokenExpired(expiresAt)) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }

    const requestInit: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(fullUrl, requestInit);

    // Only log errors for unexpected server errors (5xx), not for expected 404s or handled errors
    if (!response.ok && response.status >= 500) {
      console.error(`CustomFetch: HTTP error! status: ${response.status}, url: ${fullUrl}`);
      console.error(`CustomFetch: Response headers:`, response.headers);
    }

    const data = await getBody<T>(response);

    return { status: response.status, data, headers: response.headers } as T;
  } catch (error) {
    /*
     * An aborted request is not a failed one. Components cancel superseded requests on purpose — a
     * new filter, a new section, a new locale, an unmount, or Strict Mode's double-mount in
     * development — and reporting each of those as an error filled the console with `AbortError:
     * signal is aborted without reason` for requests that behaved exactly as intended. That noise is
     * not harmless: it is what a genuine failure has to be spotted among.
     *
     * Rethrown either way, unchanged. The callers already tell the two apart correctly: a cancel is
     * always followed by a newer request, so their `requestId` guard drops it, while a timeout has
     * nothing after it and falls through to their own error handling. This only stops the transport
     * layer from shouting about the case its callers have already handled — the same rule the 5xx
     * check above follows for expected 404s.
     */
    const name = error instanceof Error ? error.name : '';
    if (name === 'AbortError') throw error;

    if (name === 'TimeoutError') {
      console.warn(`CustomFetch: Request timed out for ${url}`);
      throw error;
    }

    const message = error instanceof Error ? error.message : String(error);
    console.error(`CustomFetch: Fetch error for ${url}: [${name}] ${message}`);
    throw error;
  }
};
